import { JobAction } from '~/interface/Job'
import ThermalPrintJob from '~/class/printer/ThermalPrintJob'
import ThermalPrintVoidItems from '~/class/printer/ThermalPrintVoidItems'
import ThermalPrintMoveItems from '~/class/printer/ThermalPrintMoveItems'
import { PLAN, currentPlan } from '~/constants/packagePlan'

// Cashier (printer 0) copy of an item-level void. The kitchen slips are split
// per station by item tag; the cashier needs one slip with EVERY voided item +
// reason. Sent to /printcustom (the receipt printer), independent of the
// kitchen-print plan so shops without kitchen printers still get the record.
function printVoidToCashier(job: any) {
  const json2: Record<string, number> = (job?.json2 as any) || {}
  const items = (Array.isArray(job?.__items) ? job.__items : []).map((it: any) => {
    const qty = Math.abs(Number(json2[it.uuid] || 0)) || Number(it.quantity || 0)
    return { name: it.name || '', qty }
  })

  if (items.length === 0) return

  new ThermalPrintVoidItems('/printcustom')
    .printVoidItemsCashier({
      alias: job?.alias || '-',
      employee: job?.employee?.name || 'unknown',
      reason: job?.__reason || '',
      voidedAt: job?.timestamp || undefined,
      toppingOnly: job?.type === JobAction.VOID_TOPPING,
      items,
    })
    .print()
}

export default function printJob(job: any) {
  console.log('thermal printer JOB run in plugin', job)
  const shopStore = usePosShopStore()
  const productsStore = usePosProductsStore()
  const tableInstanceStore = usePosTableInstanceStore()
  const employeeStore = usePosEmployeesStore()

  // Resolve the guest count at PRINT time. Order:
  //   1) job.guests — an OPENING order carries the count the cashier entered.
  //      A re-add on an existing table re-sends 0, so treat 0 as "not set" and
  //      fall through (don't trust it).
  //   2) TableStates — the current truth / cashier override for the table.
  //   3) jobs.tables — the computed table instance.
  //   4) 0 — nothing found; the slip just omits the Customer line.
  // Key: job.alias is the app2 table key, already "T"-normalised by JobBuilder;
  // mobile orders can arrive with a bare number (e.g. "71"), so prefix a "T".
  // Fully defensive: any lookup miss / bad shape falls back to 0 and never throws
  // — the app must never break over a guest count.
  const settingsStore = useSettingsStore()
  let guests = 0
  try {
    const jobGuests = Number(job?.guests) || 0
    if (jobGuests > 0) {
      guests = jobGuests
    } else {
      const jobsStore = usePosJobsStore()
      const tableStatesStore = usePosTableStatesStore()
      const rawAlias = String(job?.alias ?? '')
      const tableKey = /^\d+$/.test(rawAlias) ? `T${rawAlias}` : rawAlias
      guests = Number(
        tableStatesStore.tablesState?.[tableKey]?.guests
        ?? (jobsStore.tables as any)?.[tableKey]?.guests
        ?? 0
      ) || 0
    }
  } catch (e) {
    console.warn('kitchen guest count resolve failed, defaulting to 0', e)
    guests = 0
  }
  const options = {
    storeId: shopStore.getStoreId || 2,
    guests: settingsStore.enableCustomerMode ? guests : 0,
    billing: tableInstanceStore.summaryBillingMoney,
    productsStore,
    showPrice: settingsStore.printShowPrice,
  }

  console.log('options=>', options)

  const newJob = {
    ...job,
  }

  const shouldPrintToKitchen = (() => {
    if (job?.toKitchen === false) {
      return false
    }

    if (typeof job?.json === 'string') {
      try {
        const order = JSON.parse(job.json)
        if (order?.noPrint === true) {
          return false
        }
      } catch (_) {
        // Ignore malformed json and fallback to other checks.
      }
    }

    return true
  })()

  const canPrintKitchen = currentPlan() >= PLAN.KITCHEN_PRINT_MIN

  switch (job.type) {
    case JobAction.ADD:
      if (!shouldPrintToKitchen) break
      if (!canPrintKitchen) break
      newJob.json = typeof job.json === 'string' ? JSON.parse(job.json) : job.json
      new ThermalPrintJob('/printKitchen').printToKitchen(newJob, options).print()
      break
    case JobAction.MOVE_TO:
      if (!useSettingsStore().printMoveTable) break
      if (!canPrintKitchen) break
      new ThermalPrintMoveItems('/printKitchen').printMoveItems(job, options).print()
      break
    // VOID and VOID_TOPPING share the kitchen void slip — the topping void ships
    // descriptive __items ("item (topping)") so the same printer renders it.
    case JobAction.VOID:
    case JobAction.VOID_TOPPING:
      // Cashier copy — opt-in per shop from the void modal. Independent of the
      // kitchen toggle and the kitchen-print plan. Topping voids print the same
      // slip; their __items are already named "item (topping)".
      if (job?.__copyToCashier) printVoidToCashier(job)

      if (job?.__noPrint) break

      // Then per-station kitchen void slips (requires kitchen-print plan).
      if (!canPrintKitchen) break

      new ThermalPrintVoidItems('/printVoid').printVoidItems(job, options).print()
      break
    default:
      console.log('thermal printer JOB run in plugin not match action..')
      break
  }
}
